import { useState, useRef, useEffect } from 'react';
import {
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  collection,
  serverTimestamp,
} from 'firebase/firestore';
import { ref, deleteObject, listAll } from 'firebase/storage';
import { db, storage } from '../config/firebase';
import { useNavigate } from 'react-router-dom';
import { ImageUploader } from './ImageUploader';
import './PostEditor.css';

export function PostEditor({ post = null }) {
  const navigate = useNavigate();
  const isEditing = !!post;
  const textareaRef = useRef(null);

  const [title, setTitle] = useState(post?.title || '');
  const [content, setContent] = useState(post?.content || '');
  const [featuredImage, setFeaturedImage] = useState(post?.featuredImage || '');
  const [published, setPublished] = useState(post?.published ?? false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (post) {
      setTitle(post.title || '');
      setContent(post.content || '');
      setFeaturedImage(post.featuredImage || '');
      setPublished(post.published ?? false);
    }
  }, [post]);

  const handleSave = async () => {
    if (!content.trim()) {
      setError('Content is required');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const postData = {
        title: title.trim(),
        content: content.trim(),
        featuredImage,
        published,
        updatedAt: serverTimestamp(),
      };

      if (isEditing) {
        await updateDoc(doc(db, 'posts', post.id), postData);
      } else {
        postData.createdAt = serverTimestamp();
        await addDoc(collection(db, 'posts'), postData);
      }

      navigate('/admin');
    } catch (err) {
      console.error('Error saving post:', err);
      setError('Failed to save post');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!isEditing) return;

    const confirmed = window.confirm(
      'Are you sure you want to delete this post? This action cannot be undone.'
    );

    if (!confirmed) return;

    setDeleting(true);
    setError('');

    try {
      // Delete associated images from storage
      const storageRef = ref(storage, `posts/${post.id}`);
      try {
        const listResult = await listAll(storageRef);
        await Promise.all(
          listResult.items.map((itemRef) => deleteObject(itemRef))
        );
      } catch (storageErr) {
        // Folder might not exist, continue with post deletion
        console.log('No images to delete or error:', storageErr);
      }

      // Delete the post document
      await deleteDoc(doc(db, 'posts', post.id));
      navigate('/admin');
    } catch (err) {
      console.error('Error deleting post:', err);
      setError('Failed to delete post');
      setDeleting(false);
    }
  };

  const handleCancel = () => {
    navigate('/admin');
  };

  const insertImageAtCursor = (url) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const imageMarkdown = `![](${url})`;

    const newContent =
      content.substring(0, start) + imageMarkdown + content.substring(end);

    setContent(newContent);

    // Set cursor position after the inserted image
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(
        start + imageMarkdown.length,
        start + imageMarkdown.length
      );
    }, 0);
  };

  return (
    <div className="post-editor">
      <h1>{isEditing ? 'Edit Post' : 'New Post'}</h1>

      {error && <div className="post-editor__error text-sans">{error}</div>}

      <div className="form-group">
        <label htmlFor="title">Title (optional)</label>
        <input
          type="text"
          id="title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Enter post title..."
        />
      </div>

      <div className="form-group">
        <label>Featured Image (optional)</label>
        {featuredImage ? (
          <div className="post-editor__featured-preview">
            <img src={featuredImage} alt="Featured" />
            <button
              type="button"
              onClick={() => setFeaturedImage('')}
              className="btn btn-ghost"
            >
              Remove
            </button>
          </div>
        ) : (
          <ImageUploader
            postId={post?.id || 'new'}
            onUploadComplete={setFeaturedImage}
            label="Upload featured image"
          />
        )}
      </div>

      <div className="form-group">
        <label htmlFor="content">Content</label>
        <textarea
          ref={textareaRef}
          id="content"
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="Write your post content... (supports **bold**, *italic*, [links](url), and ![images](url))"
          rows={12}
        />
        <div className="post-editor__content-actions">
          <ImageUploader
            postId={post?.id || 'new'}
            onUploadComplete={insertImageAtCursor}
            label="Insert inline image"
          />
        </div>
      </div>

      <div className="form-group">
        <div className="checkbox-wrapper">
          <input
            type="checkbox"
            id="published"
            checked={published}
            onChange={(e) => setPublished(e.target.checked)}
          />
          <label htmlFor="published">Published</label>
        </div>
      </div>

      <div className="post-editor__actions">
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || deleting}
          className="btn btn-primary"
        >
          {saving ? 'Saving...' : 'Save'}
        </button>

        <button
          type="button"
          onClick={handleCancel}
          disabled={saving || deleting}
          className="btn btn-secondary"
        >
          Cancel
        </button>

        {isEditing && (
          <button
            type="button"
            onClick={handleDelete}
            disabled={saving || deleting}
            className="btn btn-danger"
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </button>
        )}
      </div>
    </div>
  );
}
